import log from 'loglevel'
import {
  AutocompleteInput,
  BooleanInput,
  Datagrid,
  type FieldProps,
  type FilterPayload,
  Form,
  FunctionField,
  type Identifier,
  Labeled,
  ListBase,
  Loading,
  Pagination,
  type RaRecord,
  ReferenceField,
  SimpleShowLayout,
  type SortPayload,
  TextField,
  useGetList,
  useListContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useTranslate,
  useUpdate
} from 'react-admin'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  DateInput,
  NumberInput,
  TextInput,
  TimeInput,
  useScroll
} from 'components/Util'
import React from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import {
  compact,
  fill,
  find,
  groupBy,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  join,
  keys,
  last,
  map,
  size,
  slice,
  split,
  startsWith,
  toNumber,
  uniqBy
} from 'lodash/fp'
import {
  type Partner,
  type PartnerUser,
  type Product,
  type Criterion,
  type UserAttribute,
  UserStatus
} from '@mzc-watchover/adminapp-appsync'
import { assign } from 'lodash'
import { ScheduleButton } from 'components/Schedule'
import { addDays, format, parseISO } from 'date-fns/fp'
import {
  cancelUserReserve,
  storeUsereReserve
} from 'components/DataProvider/functions'
import { useGetUserReserve } from 'components/DataProvider/hooks'

type SearchParams = {
  name: string
  address: string
  place: string
}

type PartnerUserSub = Partial<PartnerUser> & {
  id: string
  contractor: { name?: string; address?: string }
}
const SearchButton: React.FC<{
  setFilter: (filter: FilterPayload) => void
  idLabel: string | undefined
}> = ({ setFilter, idLabel }) => {
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid, dirtyFields } = useFormState()
  const translate = useTranslate()
  const onSubmit = (): void => {
    const { ...data } = getValues() as SearchParams
    const filter = {}
    for (const field of keys(dirtyFields)) {
      assign(filter, {
        [field]:
          field !== 'attributes'
            ? data[field]
            : [{ key: idLabel, value: data[field] }]
      })
    }
    setFilter(filter)
  }
  React.useEffect(() => {
    setEnable(isValid)
  }, [errors, isValid])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.users.edit.buttons.search')}
      </Typography>
    </Button>
  )
}
const SubmitButton: React.FC<{
  record: PartnerUserSub
  setRecord: (data: PartnerUserSub) => void
  schedules: string[] | null | undefined
  criterion: Criterion | null | undefined
  enable: boolean
  setEnable: (enable: boolean) => void
  partner: Partial<Partner>
  reserve?: boolean
}> = ({
  record,
  setRecord,
  schedules,
  criterion: currentCriterion,
  reserve: reserveArg,
  enable,
  setEnable,
  partner: partnerData
}) => {
  const { products } = partnerData
  const partner = partnerData.id as string
  const reserve = reserveArg ?? false
  const {
    data: reserveData,
    isLoading,
    refetch
  } = useGetUserReserve(partner, record.id)
  const [data, setData] = React.useState({})
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const { errors } = useFormState()
  const [update] = useUpdate(undefined, undefined, {
    onSettled: (data: PartnerUserSub | undefined) => {
      if (!isNil(data)) setRecord(data)
    }
  })
  const [open, setOpen] = React.useState(false)
  const [openCancel, setOpenCancel] = React.useState(false)
  const {
    stop,
    phone,
    family,
    criterion,
    criterionsOwn,
    product,
    reserve: reserveValue
  } = getValues()
  const {
    stop: currentStop,
    phone: currentPhone,
    families,
    start,
    status,
    criterions
  } = record
  const [currentSchedules, setCurrentSchedules] = React.useState<
    string[] | null | undefined
  >(record.schedules)
  const currentFamily =
    !isNil(families) && !isNil(families[0]) ? families[0] : undefined
  const onSubmit = (): void => {
    setEnable(false)
    if (!reserve) {
      const { id } = record
      setCurrentSchedules(schedules)
      log.info({ data })
      update('users', {
        id,
        data,
        previousData: record
      }).catch(() => {})
    } else {
      if (isNil(reserveData)) {
        setOpen(true)
      } else {
        setOpenCancel(true)
      }
    }
  }
  const onSubmitReserve = (): void => {
    setOpen(false)
    const { id } = record
    storeUsereReserve(partner, { id, ...data }, reserveValue.date)
      .then(() => {
        refetch()
      })
      .catch(() => {})
  }
  const onSubmitCancelReserve = (): void => {
    setOpenCancel(false)
    const { date } = reserveData
    cancelUserReserve(partner, record.id, date)
      .then(() => {
        refetch().catch(() => {})
        setData({})
      })
      .catch(() => {})
  }
  React.useEffect(() => {
    if (!isEmpty(errors)) return
    const update = {}
    if (!isNil(stop) && stop !== currentStop)
      assign(update, {
        stop,
        status: isNil(start)
          ? status
          : !isNil(stop)
          ? UserStatus.stop
          : UserStatus.inUse
      })
    if (!isNil(phone) && phone !== currentPhone) {
      assign(update, { phone })
    }
    if (!isNil(currentFamily)) {
      const {
        name: currentFamilyName,
        nameKana: currentFamilyNameKana,
        phone: currentFamilyPhone,
        postalCode: currentFamilyPostalCode
      } = currentFamily
      const familyData = JSON.parse(JSON.stringify(currentFamily))
      if (!isNil(family.name) && family.name !== currentFamilyName) {
        assign(familyData, { name: family.name })
      }
      if (
        !isNil(family.nameKana) &&
        family.nameKana !== currentFamilyNameKana
      ) {
        assign(familyData, { nameKana: family.nameKana })
      }
      if (!isNil(family.phone) && family.phone !== currentFamilyPhone) {
        assign(familyData, { phone: family.phone })
      }
      if (
        !isNil(family.postalCode) &&
        family.postalCode !== currentFamilyPostalCode
      ) {
        assign(familyData, { postalCode: family.postalCode })
      }
      if (!isEmpty(familyData) && !isEqual(familyData)(currentFamily)) {
        assign(update, { families: [familyData] })
      }
    }
    if (!isNil(currentCriterion)) {
      const { start, end, continuousLimit } = criterion
      const criterionData = JSON.parse(JSON.stringify(currentCriterion))
      if (!isNil(start) && start !== currentCriterion.start) {
        assign(criterionData, { start })
      }
      if (!isNil(end) && end !== currentCriterion.end) {
        assign(criterionData, { end })
      }
      if (
        !isNil(continuousLimit) &&
        continuousLimit !== currentCriterion.continuousLimit
      ) {
        assign(criterionData, { continuousLimit })
      }
      if (
        !isEmpty(criterionData) &&
        !isEqual(criterionData)(currentCriterion)
      ) {
        assign(update, {
          criterions: map((item: Criterion) => {
            const { start, end, continuousLimit } = criterionData
            if (!isNil(start)) item.start = start
            if (!isNil(end)) item.end = end
            if (!isNil(continuousLimit)) item.continuousLimit = continuousLimit
            return item
          })(JSON.parse(JSON.stringify(criterions)))
        })
      }
    }
    if (record.criterionsOwn !== criterionsOwn) {
      assign(update, { criterionsOwn })
      if (criterionsOwn as boolean) {
        assign(update, {
          criterions: fill(0)(12)(criterion)(new Array(12))
        })
      } else {
        assign(update, { criterions: [] })
      }
    }
    if (
      !isEqual(isNil(schedules) ? [] : schedules)(
        isNil(currentSchedules) ? [] : currentSchedules
      )
    ) {
      assign(update, { schedules: isNil(schedules) ? [] : schedules })
    }
    if (record.product !== product) {
      assign(update, { product })
    }
    if (!isEqual(update)(data)) {
      setEnable(!isEmpty(update))
      setData(update)
    }
  }, [
    errors,
    data,
    stop,
    phone,
    currentFamily,
    family.name,
    family.nameKana,
    family.phone,
    family.postalCode,
    currentCriterion,
    criterionsOwn,
    criterion,
    criterion?.start,
    criterion?.end,
    criterion?.continuousLimit,
    currentSchedules,
    schedules,
    record,
    product
  ])
  const handleClose = (): void => {
    setOpen(false)
  }
  if (isLoading) return null
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="large"
        sx={{ borderRadius: '10px' }}
        fullWidth
        disabled={reserve && !isNil(reserveData) ? false : !enable}
        onClick={onSubmit}>
        <Typography variant="button">
          {translate(
            `resources.users.show.buttons.${
              !reserve
                ? 'submit'
                : isNil(reserveData)
                ? 'reserve'
                : 'cancelReserve'
            }`
          )}
        </Typography>
      </Button>
      <Dialog onClose={handleClose} open={open} maxWidth="xs">
        <DialogTitle>
          {translate('resources.users.show.reserve.title')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center">
            <Box>
              <DateInput
                source="reserve.date"
                fields="resources.users.show"
                minDate={addDays(1)(Date.now())}
              />
            </Box>
            <Button
              onClick={onSubmitReserve}
              variant="contained"
              disabled={
                isNil(reserveValue) ||
                isNil(reserveValue.date) ||
                isEmpty(reserveValue.date)
              }
              sx={{ mt: '2em', ml: '2em' }}>
              {translate('resources.users.show.reserve.save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog onClose={handleClose} open={openCancel} fullWidth>
        <DialogTitle>
          {translate('resources.users.show.cancelReserve.title')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Box>
              {!isNil(reserveData) &&
                map((key: string) => {
                  if (key === 'id' || key === 'status') return null
                  if (key === 'families') {
                    const current = (record.families as any)[0]
                    return map((subKey: string) => {
                      if (
                        isNil(reserveData.data.families[0][subKey]) ||
                        isEmpty(reserveData.data.families[0][subKey]) ||
                        current[subKey] === reserveData.data.families[0][subKey]
                      ) {
                        return null
                      }
                      return (
                        <Grid container key={`${key}-${subKey}`}>
                          <Grid xs={5}>
                            <Typography>{`${translate(
                              `resources.users.fields.families.0.${subKey}`
                            )}`}</Typography>
                          </Grid>
                          <Grid xs={7}>
                            <Typography>{`${
                              reserveData.data.families[0][subKey] as string
                            }`}</Typography>
                          </Grid>
                        </Grid>
                      )
                    })(keys(reserveData.data.families[0]))
                  }
                  if (key === 'schedules') {
                    return (
                      <Grid container key={key}>
                        <Grid xs={5}>
                          <Typography>{`${translate(
                            `resources.users.show.fields.${key}`
                          )}`}</Typography>
                        </Grid>
                        <Grid xs={7}>
                          <Typography>
                            {`${join('、')(
                              map((value: string) =>
                                format('y年M月d日')(parseISO(value))
                              )(slice(0, 5)(reserveData.data[key]))
                            )}
                            ${size(reserveData.data[key]) > 5 ? '...' : ''}`}
                          </Typography>
                        </Grid>
                      </Grid>
                    )
                  }
                  if (key === 'criterions') {
                    const item = head(reserveData.data[key]) as Criterion
                    const current = head(record.criterions) as Criterion
                    return map((subKey: string) => {
                      if (
                        isNil(item[subKey]) ||
                        current[subKey] === item[subKey]
                      ) {
                        return null
                      }
                      return (
                        <Grid container key={`${key}-${subKey}`}>
                          <Grid xs={5}>
                            <Typography>{`${translate(
                              `resources.users.show.fields.criterion.${subKey}`
                            )}`}</Typography>
                          </Grid>
                          <Grid xs={7}>
                            <Typography>
                              {item[subKey as keyof typeof item]}
                            </Typography>
                          </Grid>
                        </Grid>
                      )
                    })(keys(item))
                  }
                  return (
                    <Grid container key={key}>
                      <Grid xs={5}>
                        <Typography>{`${translate(
                          `resources.users.show.fields.${key}`
                        )}`}</Typography>
                      </Grid>
                      <Grid xs={7}>
                        <Typography>{`${
                          key === 'stop'
                            ? format('y年M月d日')(
                                parseISO(reserveData.data[key])
                              )
                            : key === 'product' && !isNil(products)
                            ? (products[
                                toNumber(
                                  last(split('-')(reserveData.data[key]))
                                )
                              ]?.contactOrganization as string)
                            : (reserveData.data[key] as string)
                        }`}</Typography>
                      </Grid>
                    </Grid>
                  )
                })(keys(reserveData.data))}
            </Box>
            <Box display="flex" justifyContent="center">
              <Button
                onClick={(): void => {
                  setOpenCancel(false)
                }}
                variant="outlined"
                sx={{
                  mt: '2em'
                }}>
                {translate('resources.users.show.cancelReserve.close')}
              </Button>
              <Button
                onClick={onSubmitCancelReserve}
                variant="contained"
                sx={{
                  ml: '1em',
                  mt: '2em'
                }}>
                {translate('resources.users.show.cancelReserve.exec')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
const UserEditForm: React.FC<{
  setFilter: (filter: FilterPayload) => void
  idLabel: string | undefined
  partner: Partial<Partner> | undefined
}> = ({ setFilter, idLabel, partner }) => {
  const translate = useTranslate()
  const validate = (values: any): Record<string, string> => {
    const { name, address, place } = values as SearchParams
    const errors: { children?: string } = {}
    if (isNil(name) && isNil(address) && isNil(place)) {
      errors.children = 'ra.validation.required'
    }
    return errors
  }
  if (isNil(partner)) return <></>
  const { products } = partner
  const solar = !isNil(
    find(
      (item: Product) =>
        !isNil(item.analytics) && startsWith('solar')(item.analytics)
    )(products)
  )
  return (
    <Form
      validate={validate}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={{
        name: null,
        address: null,
        place: null,
        attributes: null
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Box sx={{ width: '1280px' }}>
          <Box
            sx={{
              marginLeft: '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {translate('resources.users.edit.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="name"
                  placeholder="中電太郎"
                  fields="resources.users.edit.fields"
                />
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="address"
                  placeholder="愛知県名古屋市中区"
                  fields="resources.users.edit.fields"
                />
              </Grid>
              {!solar && (
                <Grid
                  item
                  xs={8}
                  sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                  <TextInput
                    source="place"
                    placeholder="KDX名古屋ビル"
                    fields="resources.users.edit.fields"
                  />
                </Grid>
              )}
              {!isNil(idLabel) && (
                <Grid
                  item
                  xs={8}
                  sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                  <TextInput
                    source="attributes"
                    placeholder="1234567890"
                    label={idLabel}
                  />
                </Grid>
              )}
              <Grid item xs={8} sx={{ marginTop: '0.5em' }}>
                <SearchButton setFilter={setFilter} idLabel={idLabel} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Form>
  )
}

const AttributeField: React.FC<FieldProps> = () => {
  const record = useRecordContext()
  const { attributes } = record
  if (isNil(attributes) || isNil(attributes[0])) return <></>
  const { key, value } = attributes[0]
  return <TextField record={{ [key]: value }} source={key} />
}
const UserListContent: React.FC<{
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
  solar: boolean
}> = ({ setRecord, scrollRef, solar }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { data, total, isLoading } = useListContext()
  React.useEffect(() => {
    if (isLoading) return
    if (total < 2) {
      setRecord(data[0])
      if (total === 0) {
        notify('resources.users.show.notifications.notFound')
      }
    }
  }, [isLoading, data, total])
  const select = (
    _id: Identifier,
    _resource: string,
    record: RaRecord
  ): string => {
    scrollRef?.current?.scrollIntoView()
    setRecord(record as PartnerUserSub)
    return 'toggleSelection'
  }
  const ref = useScroll(total)
  if (isLoading) return <Loading />
  if (total < 2 || isNil(data)) return <></>
  let attributeKey
  if (!isNil(data[0])) {
    const { attributes } = data[0]
    if (!isNil(attributes) && !isNil(attributes[0])) {
      attributeKey = attributes[0].key
    }
  }
  return (
    <Grid
      container
      justifyContent="center"
      spacing={2}
      sx={{ marginTop: '0.5em' }}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {translate('resources.users.show.list.title')}
        </Typography>
        {!solar ? (
          <Datagrid
            rowClick={select}
            bulkActionButtons={false}
            sx={{ marginTop: '0.5em' }}>
            {!isNil(attributeKey) && <AttributeField source={attributeKey} />}
            <TextField source="name" />
            <TextField source="address" />
            <TextField source="place" />
            <TextField source="placeNumber" />
          </Datagrid>
        ) : (
          <Datagrid
            rowClick={select}
            bulkActionButtons={false}
            sx={{ marginTop: '0.5em' }}>
            {!isNil(attributeKey) && <AttributeField source={attributeKey} />}
            <TextField source="name" />
            <TextField source="address" />
          </Datagrid>
        )}
        {total > 10 && <Pagination />}
        <div ref={ref} />
      </Grid>
    </Grid>
  )
}
const UserList: React.FC<{
  filter: FilterPayload
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
  solar: boolean
}> = ({ filter, setRecord, scrollRef, solar }) => {
  const sort: SortPayload = { field: 'name', order: 'ASC' }
  return (
    <ListBase resource="users" sort={sort} filter={filter}>
      <UserListContent
        setRecord={setRecord}
        scrollRef={scrollRef}
        solar={solar}
      />
    </ListBase>
  )
}
const UserTextField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
}> = ({ source, ownLabel }) => {
  const record = useRecordContext<PartnerUser>()
  const { start, analytics } = record
  return (
    <Labeled
      label={
        !isNil(ownLabel)
          ? ownLabel
          : isNil(analytics) || !startsWith('solar')(analytics)
          ? `resources.users.fields.${source}`
          : `resources.users.solarFields.${source}`
      }
      sx={{ fontVariant: 'body1' }}>
      {source === 'start' && (isNil(start) || isEmpty(start)) ? (
        <Typography variant="body1">利用開始前</Typography>
      ) : (
        <TextField source={source} sx={{ fontVariant: 'body1' }} />
      )}
    </Labeled>
  )
}
const UserReferenceField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
  reference: string
  children: JSX.Element
}> = ({ source, ownLabel, reference, children }) => {
  return (
    <Labeled
      label={!isNil(ownLabel) ? ownLabel : `resources.users.fields.${source}`}
      sx={{ fontVariant: 'body1' }}>
      <ReferenceField
        reference={reference}
        source={source}
        sx={{ fontVariant: 'body1' }}>
        {children}
      </ReferenceField>
    </Labeled>
  )
}
const UserRecordShow: React.FC<{
  record: PartnerUserSub
}> = ({ record }) => {
  const translate = useTranslate()
  const { stop, end, attributes, status, analytics, contractor } = record
  const idItem = find((item: UserAttribute) => item.key !== 'fileId')(
    attributes
  )
  const statusLabel = {
    [UserStatus.inUse]: '提供中',
    [UserStatus.switching]: '提供中',
    [UserStatus.switchingInWaitingResult]: '提供中',
    [UserStatus.switchingInWaitingStart]: '提供中',
    [UserStatus.beforeStart]: '提供準備中',
    [UserStatus.waitingResult]: '提供準備中',
    [UserStatus.waitingStart]: '提供準備中',
    [UserStatus.stop]: '解約予定',
    [UserStatus.end]: '解約済み'
  }
  const showRecord = {
    ...record,
    idItem: !isNil(idItem) ? idItem.value : undefined,
    status: statusLabel[status as keyof typeof statusLabel]
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {translate('resources.users.show.record.title')}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="name" label={false} />
          {!isNil(idItem) && (
            <UserTextField
              source="idItem"
              label={false}
              ownLabel={idItem.key}
            />
          )}
          <UserReferenceField
            reference="organizations"
            source="product"
            label={false}>
            <FunctionField
              source="contactOrganization"
              sx={{ fontVariant: 'body1' }}
              render={(record: Product) => {
                const { contactOrganization } = record
                return `${contactOrganization as string}さま`
              }}
            />
          </UserReferenceField>
          <UserTextField source="address" label={false} />
          {(isNil(analytics) || !startsWith('solar')(analytics)) && (
            <Grid container>
              <Grid item xs={8}>
                <UserTextField source="place" label={false} />
              </Grid>
              <Grid item xs={4}>
                <UserTextField source="placeNumber" label={false} />
              </Grid>
            </Grid>
          )}
          <UserTextField source="phone" label={false} />
          {!isNil(contractor) && !isEmpty(contractor.name) && (
            <Grid container>
              <Grid item xs={12}>
                <UserTextField source="contractor.name" label={false} />
              </Grid>
              <Grid item xs={12}>
                <UserTextField source="contractor.address" label={false} />
              </Grid>
            </Grid>
          )}
          {!isNil(analytics) && startsWith('solar')(analytics) && (
            <Grid container>
              <Grid item xs={12}>
                <UserTextField source="spin" label={false} />
              </Grid>
              <Grid item xs={12}>
                <UserTextField source="classification" label={false} />
              </Grid>
            </Grid>
          )}
        </SimpleShowLayout>
      </Grid>
      {(isNil(analytics) || !startsWith('solar')(analytics)) && (
        <Grid item xs={4}>
          <SimpleShowLayout record={showRecord}>
            <UserTextField source="families.0.name" label={false} />
            <UserTextField source="families.0.nameKana" label={false} />
            <UserTextField source="families.0.phone" label={false} />
            <UserTextField source="families.0.postalCode" label={false} />
          </SimpleShowLayout>
        </Grid>
      )}
      <Grid item xs={2}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="status" label={false} />
          <UserTextField source="start" label={false} />
          {!isNil(stop) && !isEmpty(stop) && (
            <UserTextField source="stop" label={false} />
          )}
          {!isNil(end) && !isEmpty(end) && (
            <UserTextField source="end" label={false} />
          )}
        </SimpleShowLayout>
      </Grid>
    </Grid>
  )
}
const ContactInput: React.FC<{ partner: Partial<Partner> }> = ({ partner }) => {
  const { getValues } = useFormContext()
  const { product } = getValues()
  const [choices, setChoices] = React.useState<
    Array<{ id: string; name: string }>
  >([])
  const { id, products } = partner
  React.useEffect(() => {
    if (!isEmpty(choices)) return
    const index = toNumber(last(split('-')(product)))
    const { name } =
      !isNil(products) && !isNil(products[index])
        ? products[index]
        : { name: undefined }
    const byName = groupBy<Product & { index: number }>('name')(
      // @ts-expect-error ts(2339)
      map.convert({ cap: false })((item: Product, index: number) => ({
        ...item,
        index
      }))(products)
    )
    if (isNil(name)) return
    const items = compact(
      map((item: Product & { index: number }) => {
        const { contactOrganization, index } = item
        return !isNil(contactOrganization) && !isEmpty(contactOrganization)
          ? { id: `${id as string}-${index}`, name: contactOrganization }
          : undefined
      })(byName[name])
    )
    setChoices(items)
  }, [products])
  return (
    <Grid container justifyContent="center" alignItems="center" spacing={0}>
      <Grid item xs={4}>
        <AutocompleteInput
          source="product"
          choices={choices}
          label="resources.users.show.fields.product"
        />
      </Grid>
    </Grid>
  )
}
const CriterionInput: React.FC<{
  record: PartnerUserSub
}> = ({ record }) => {
  const { getValues } = useFormContext()
  const { criterionsOwn } = getValues()
  const disabled = isNil(criterionsOwn) || !(criterionsOwn as boolean)
  const { continuousLimit } =
    !isNil(record.criterionsOwn) &&
    record.criterionsOwn &&
    !isNil(record.criterions) &&
    !isNil(record.criterions[0])
      ? record.criterions[0]
      : { continuousLimit: undefined }
  return (
    <>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={0}
        sx={{ mt: '1em' }}>
        <Grid item xs={4}>
          <BooleanInput
            source="criterionsOwn"
            label="resources.users.show.fields.criterion.criterionsOwn"
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={0}
        sx={{ mt: '0em' }}>
        <Grid item xs={4}>
          <TimeInput
            source="criterion.start"
            fields="resources.users.show.fields"
            minutesStep={30}
            readOnly={disabled}
          />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={0}
        sx={{ mt: '0em' }}>
        <Grid item xs={4}>
          <TimeInput
            source="criterion.end"
            fields="resources.users.show.fields"
            minutesStep={30}
            readOnly={disabled}
          />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={0}
        sx={{ mt: '0em' }}>
        <Grid item xs={4}>
          <NumberInput
            source="criterion.continuousLimit"
            fields="resources.users.show.fields"
            fullWidth
            min={1}
            max={
              isNil(continuousLimit) || continuousLimit <= 7
                ? 7
                : continuousLimit
            }
            readOnly={disabled}
          />
        </Grid>
      </Grid>
    </>
  )
}
const UserShowForm: React.FC<{
  filter: FilterPayload
  partner: Partial<Partner> | undefined
}> = ({ filter, partner }) => {
  const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const [enable, setEnable] = React.useState(false)
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const [schedules, setSchedules] = React.useState<string[] | null | undefined>(
    record?.schedules
  )
  const [criterion, setCriterion] = React.useState<Criterion | undefined>(
    undefined
  )
  const [criterionsOwn, setCriterionsOwn] = React.useState(false)
  const family =
    !isNil(record) && !isNil(record.families) ? record.families[0] : undefined
  const schema = z
    .object({
      stop: z.string().nullable(),
      phone: z
        .string()
        .min(11, { message: 'too small' })
        .max(11, { message: 'too large' }),
      family: z.object({
        name: z.string().min(1),
        nameKana: z.string().min(1),
        phone: z
          .string()
          .min(11, { message: 'too small' })
          .max(11, { message: 'too large' }),
        postalCode: z
          .string()
          .min(7, { message: 'too small' })
          .max(7, { message: 'too large' })
      }),
      schedules: z.array(z.string()).optional(),
      criterion: z
        .object({
          start: z.string().nullable(),
          end: z.string().nullable(),
          continuousLimit: z.string().nullable()
        })
        .optional()
    })
    .refine(
      (record) => {
        const { criterion } = record
        if (isNil(criterion)) return true
        const { start, end, continuousLimit } = criterion
        return !isNil(start) && isNil(end) && !isNil(continuousLimit)
      },
      {
        message:
          '判定開始時間、判定終了時間、判定連続日数はすべて入力してください'
      }
    )
    .refine(
      (record) => {
        const { criterion } = record
        if (isNil(criterion)) return true
        const { start, end } = criterion
        return !isNil(start) && !isNil(end) && start < end
      },
      {
        message: '判定終了時間は判定開始時間よりも後の時間を入力してください'
      }
    )
  const ref = useScroll(record)
  const { analytics, status } = !isNil(record)
    ? record
    : { analytics: undefined, status: undefined }
  React.useEffect(() => {
    if (!isNil(record)) setSchedules(record.schedules)
  }, [record])
  if (isNil(partner)) return <></>
  const { products } = partner
  const solar = !isNil(
    find(
      (item: Product) =>
        !isNil(item.analytics) && startsWith('solar')(item.analytics)
    )(products)
  )
  React.useEffect(() => {
    let value =
      isNil(record) ||
      isNil(record.criterions) ||
      isNil(record.criterionsOwn) ||
      !record.criterionsOwn
        ? undefined
        : !isNil(record.criterionsAll) && record.criterionsAll
        ? record.criterions[0]
        : size(
            uniqBy<Criterion>(
              (item: Criterion) =>
                `${item.start as string}-${item.end as string}-${
                  item.continuousLimit as number
                }`
            )(record.criterions)
          ) > 1
        ? undefined
        : record.criterions[0]
    setCriterionsOwn(!isNil(value))
    if (isNil(value) && !isNil(record) && !isNil(products)) {
      const productIndex = toNumber(last(split('-')(record.product)))
      let product = products[productIndex]
      if (!isNil(product)) {
        let { criterions } = product
        if (size(criterions) === 0) {
          const productName = product.name
          product = find(
            (item: Product) =>
              item.name === productName && size(item.criterions) > 0
          )(products)
          if (isNil(product)) {
            if (!isEmpty(criterion)) setCriterion({})
            return
          }
          criterions = product.criterions
        }
        const { criterionsAll } = product
        if (
          (!isNil(criterionsAll) && criterionsAll) ||
          size(
            uniqBy<Criterion>(
              (item: Criterion) =>
                `${item.start as string}-${item.end as string}-${
                  item.continuousLimit as number
                }`
            )(criterions)
          ) === 1
        ) {
          value = criterions[0]
        }
      }
    }
    if (!isEqual(value)(criterion)) {
      setCriterion(value)
    }
  }, [record, products, criterion])
  if (isLoadingPermissions) return <Loading />
  return (
    <Form
      resolver={zodResolver(schema)}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={
        isNil(record) || isNil(family)
          ? {}
          : isNil(criterion)
          ? {
              stop: isEmpty(record.stop) ? null : record.stop,
              phone: record.phone,
              family,
              schedules,
              criterion: {},
              criterionsOwn,
              product: record.product
            }
          : {
              stop: isEmpty(record.stop) ? null : record.stop,
              phone: record.phone,
              family,
              schedules,
              criterion,
              criterionsOwn,
              product: record.product
            }
      }>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '1280px' }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <UserList
              filter={filter}
              setRecord={setRecord}
              scrollRef={ref}
              solar={solar}
            />
          </Box>
          {!isNil(record) && (
            <>
              <Box sx={{ marginTop: '1em' }} id="user-show-content">
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid item xs={12}>
                      {!isNil(record) ? (
                        <UserRecordShow record={record} />
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        marginBottom: '0.2em'
                      }}>
                      <DateInput
                        source="stop"
                        fields="resources.users.show.fields"
                        disabled={
                          includes('noStop')(permissions) ||
                          status === UserStatus.end
                        }
                        disablePast
                      />
                    </Grid>
                  </Grid>
                  {(isNil(analytics) || !startsWith('solar')(analytics)) && (
                    <>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="phone"
                            placeholder="09012345678"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.name"
                            placeholder="中電太郎"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.nameKana"
                            placeholder="チュウデンタロウ"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.phone"
                            placeholder="09012345678"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      {!isNil(family) && !isNil(family.postalCode) && (
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          spacing={2}
                          sx={{ marginTop: '0.1em' }}>
                          <Grid
                            item
                            xs={4}
                            sx={{
                              marginBottom: '0.2em'
                            }}>
                            <TextInput
                              source="family.postalCode"
                              placeholder="0123456"
                              fields="resources.users.show.fields"
                            />
                          </Grid>
                        </Grid>
                      )}
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <ScheduleButton
                            source="schedules"
                            label="resources.users.show.fields.schedules"
                            setSchedules={setSchedules}
                          />
                        </Grid>
                      </Grid>
                      {includes('editCriterion')(permissions) &&
                        analytics === 'activity' && (
                          <CriterionInput record={record} />
                        )}
                      {includes('changeContact')(permissions) && (
                        <ContactInput partner={partner} />
                      )}
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid item xs={3}>
                      <SubmitButton
                        record={record}
                        setRecord={setRecord}
                        schedules={schedules}
                        criterion={criterion}
                        enable={enable}
                        setEnable={setEnable}
                        partner={partner}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <SubmitButton
                        record={record}
                        setRecord={setRecord}
                        schedules={schedules}
                        criterion={criterion}
                        enable={enable}
                        setEnable={setEnable}
                        partner={partner}
                        reserve
                      />
                    </Grid>
                    <div ref={ref} />
                  </Grid>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Form>
  )
}
export const UserEdit: React.FC<{ partner: Partial<Partner> | undefined }> = ({
  partner,
  ...props
}) => {
  const [filter, setFilter] = React.useState<FilterPayload | undefined>(
    undefined
  )
  const [idLabel, setIdLabel] = React.useState<string | undefined>(undefined)
  const { data, isLoading } = useGetList<PartnerUser>('users', {
    pagination: { page: 1, perPage: 1 }
  })
  React.useEffect(() => {
    if (isLoading) return
    if (!isNil(data) && !isEmpty(data) && !isNil(data[0])) {
      const { attributes } = data[0]
      const idItem = find((item: UserAttribute) => item.key !== 'fileId')(
        attributes
      )
      if (!isNil(idItem)) {
        setIdLabel(idItem.key)
      }
    }
  }, [isLoading])
  if (isLoading) return <Loading />
  return (
    <>
      <Card raised sx={{ width: '1280px', mt: '1em', mb: '1em' }}>
        <CardContent>
          <UserEditForm
            {...props}
            setFilter={setFilter}
            idLabel={idLabel}
            partner={partner}
          />
        </CardContent>
      </Card>
      {!isNil(filter) && (
        <Card
          raised
          sx={{
            width: '1280px',
            mt: '1em',
            mb: '1em'
          }}>
          <CardContent>
            <UserShowForm {...props} filter={filter} partner={partner} />
          </CardContent>
        </Card>
      )}
    </>
  )
}
